// ============================================================
// useCommandContext — resolve the current Square Code route into safe task
// context for the command bar + Agent Workbench. Client-side inference only;
// the SERVER re-validates any project_id before it scopes anything (see
// docs/agents/COMMAND_CONTEXT_CONTRACT.md). Recognizes both legacy
// (/square/projects/:id) and Command Center routes.
// ============================================================

import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

export interface CommandContext {
  route: string
  project_id: string | null
  record_type: 'project' | 'portal' | 'task' | 'file' | 'repository' | null
  record_id: string | null
  repository: string | null
}

export interface ContextChip {
  key: string
  label: string
  value: string
}

export function resolveCommandContext(pathname: string): CommandContext {
  const base: CommandContext = { route: pathname, project_id: null, record_type: null, record_id: null, repository: null }

  const ccProject = pathname.match(/\/square\/command-center\/projects\/([^/]+)/)
  if (ccProject) return { ...base, project_id: ccProject[1], record_type: 'project', record_id: ccProject[1] }

  const legacyProject = pathname.match(/\/square\/projects\/([^/]+)/)
  if (legacyProject) return { ...base, project_id: legacyProject[1], record_type: 'project', record_id: legacyProject[1] }

  const portal = pathname.match(/\/olivium\/portals\/([^/]+)/)
  if (portal) return { ...base, record_type: 'portal', record_id: portal[1] }

  const task = pathname.match(/\/assistants\/tasks\/([^/]+)/)
  if (task) return { ...base, record_type: 'task', record_id: task[1] }

  return base
}

export function contextChips(ctx: CommandContext): ContextChip[] {
  const chips: ContextChip[] = []
  if (ctx.project_id) chips.push({ key: 'project', label: 'Project', value: ctx.project_id })
  else if (ctx.record_type) chips.push({ key: ctx.record_type, label: ctx.record_type[0].toUpperCase() + ctx.record_type.slice(1), value: ctx.record_id ?? '' })
  if (ctx.repository) chips.push({ key: 'repo', label: 'Repository', value: ctx.repository })
  const page = ctx.route.split('/').filter(Boolean).slice(-1)[0] ?? 'shell'
  chips.push({ key: 'page', label: 'Page', value: page })
  return chips
}

export function useCommandContext(): { context: CommandContext; chips: ContextChip[] } {
  const location = useLocation()
  return useMemo(() => {
    const context = resolveCommandContext(location.pathname)
    return { context, chips: contextChips(context) }
  }, [location.pathname])
}
